import { DocumentSerializerOutput } from "@serializers/documents"

const SchemaType = Object.freeze({
  article: "Article" as "Article",
  webPage: "WebPage" as "WebPage",
  person: "Person" as "Person",
  organization: "Organization" as "Organization",
})

interface SchemaAuthor {
  "@type": typeof SchemaType.person
  name?: string
  image?: string
  url?: string
}

interface SchemaOrganization {
  "@type": typeof SchemaType.organization
  name?: string
}

interface SchemaPage {
  "@context": "https://schema.org/"
  "@type": string
  author?: SchemaAuthor | SchemaOrganization
  dateModified?: string
  datePublished?: string
  url?: string
  description?: string
  image?: string
  headline?: string
  name?: string
  publisher?: SchemaAuthor | SchemaOrganization
}

interface GetJSONLDSchemaArgs
  extends Omit<SchemaPage, "@context" | "@type" | "author"> {
  authorName?: string
  authorImg?: string
  siteName?: string
  isArticle?: boolean
  isAmp?: boolean
}

export function getJSONLDSchema(params: GetJSONLDSchemaArgs) {
  const {
    authorImg,
    authorName,
    dateModified,
    datePublished,
    description,
    headline,
    image,
    isArticle,
    name,
    siteName,
    url,
    isAmp,
  } = params

  const organizationData: SchemaOrganization = {
    "@type": SchemaType.organization,
    name: siteName,
  }

  const authorData: SchemaAuthor = {
    "@type": SchemaType.person,
    name: authorImg,
    image: authorName || undefined,
    url: "https://pure.app/content/en/about-us",
  }

  const getAuthor = () => {
    if (!siteName && !authorName) {
      return
    }

    if (!isArticle) {
      return organizationData
    }

    if (authorName) {
      return authorData
    }

    return organizationData
  }

  const author = getAuthor()

  const pageData: SchemaPage = {
    author,
    "@context": "https://schema.org/",
    // @ts-ignore
    "@type": isArticle ? SchemaType.article : SchemaType.webPage,
    description,
    image,
    headline: isArticle ? headline : undefined,
    name,
    url,
    dateModified,
    datePublished,
    ...(isAmp && { publisher: author }),
  }

  const clearedData = Object.entries(pageData).reduce((acc, [k, v]) => {
    if (Boolean(v)) acc[k] = v
    return acc
  }, {})

  try {
    return JSON.stringify(clearedData)
  } catch (err) {
    console.error(err) // eslint-disable-line no-console
    return null
  }
}

interface FAQQuestionSchema {
  "@type": "Question"
  name: string
  acceptedAnswer: {
    "@type": "Answer"
    text: string
  }
}

interface FAQPageSchema {
  "@context": "https://schema.org/"
  "@type": "FAQPage"
  mainEntity: FAQQuestionSchema[]
}

export function getFaqPageJSONLDSchema(
  data: DocumentSerializerOutput[],
): string | null {
  const mainEntity: FAQQuestionSchema[] = data.map((question) => ({
    "@type": "Question",
    name: question.title,
    acceptedAnswer: {
      "@type": "Answer",
      text: question.description,
    },
  }))
  const schema: FAQPageSchema = {
    "@context": "https://schema.org/",
    "@type": "FAQPage",
    mainEntity,
  }

  try {
    return JSON.stringify(schema)
  } catch (err) {
    console.error(err) // eslint-disable-line no-console
    return null
  }
}
